import { GraphQL } from "@rpi/openapi-api";
import { Flex, RpiButton } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { AppPage } from "../../../components/AppPage";
import { PaginationV2 } from "../../../components/PaginationV2";
import { RpiPagination, RpiPaginationRow } from "../../../components/RpiPagination";
import { RpiTableV2Container, RpiTableFooter } from "../../../components/RpiTableV2Container";
import { RpiSearchFilter } from "../../../components/search-filter/RpiSearchFilter";
import { customerColumns } from "../../../configuration/columns/customer.column";
import { customerFilters } from "../../../configuration/filters/customer.filter";
import { featureFlags } from "../../../featureFlags";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiPerPage } from "../../../hooks/useRpiPerPage";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchInputs, SearchSort } from "../../../utils/graphql.util";
import { SearchBar } from "../../../components/search-filter/SearchFilterBar";

export const CustomersView: React.FC = () => {
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.AdminSearchCustomersQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInputs<GraphQL.AdminSearchCustomersQueryVariables>>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.AdminSearchCustomersQueryVariables>>();

  const pagination = useRpiPagination({ perPage: featureFlags.newTableView ? 20 : 10 });

  const { data, isLoading, error, refetch } = GraphQL.useAdminSearchCustomersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.adminSearchCustomers.totalCount),
    }
  );
  const customers = React.useMemo(() => data?.adminSearchCustomers.data, [data]);

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  return (
    <AppPage path={["Organizations"]} onReload={refetch}>
      {outlet}

      <AppPage.Section>
        <Flex.Row justify="space-between" align="flex-end">
          {featureFlags.newSearchExperience ? (
            <SearchBar
              placeholder="Search by Public ID or Admin Email"
              isLoading={isLoading}
              onSearchSubmit={setSearchGeneral}
            />
          ) : (
            <RpiSearchFilter
              placeholder="Search by Public ID (or Email)"
              filters={customerFilters}
              onChange={setSearchInputs}
              onSearch={setSearchGeneral}
            />
          )}
          <RpiButton
            width="fit"
            variant="brand-outline"
            size="small"
            children="Add new customer"
            trailingIcon
            onClick={() => navigateRoute(["customer:create"])}
          />
        </Flex.Row>

        {featureFlags.newTableView ? (
          <MantineProvider theme={rpaTheme}>
            <RpiTableV2Container data-testid="customers-table">
              <RpiTable
                variant="normalV2"
                emptyMessage={
                  searchInputs?.length || searchGeneral ? "No customers found." : "Your customers will appear here."
                }
                isLoading={isLoading}
                error={error}
                perPage={customers?.length}
                data={customers}
                columns={customerColumns}
                withSortBy={{ onChange: setSortInput }}
              />
              <RpiTableFooter>
                <PaginationV2 isLoading={isLoading} pagination={pagination} label="Customers" />
              </RpiTableFooter>
            </RpiTableV2Container>
          </MantineProvider>
        ) : (
          <>
            <RpiTable
              emptyMessage={
                searchInputs?.length || searchGeneral ? "No customers found." : "Your customers will appear here."
              }
              isLoading={isLoading}
              error={error}
              perPage={pagination.perPage}
              data={customers}
              columns={customerColumns}
              withSortBy={{ onChange: setSortInput }}
            />
            <RpiPaginationRow>
              <RpiPagination pagination={pagination} />
              <RpiPerPage perPage={pagination.perPage} setPerPage={pagination.setPerPage} />
            </RpiPaginationRow>
          </>
        )}
      </AppPage.Section>
    </AppPage>
  );
};
